/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { useQuery } from '@apollo/client';

import { useLocation } from 'js/lib/useRouter';
import { get } from 'js/lib/user';

import OrgScopedEnterpriseSkillsContext from 'bundles/page-config-common/contexts/enterprise-skills/EnterpriseOrgScopedSkillsContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import { QUERY_PARAM_VALUES } from 'bundles/unified-home-common/constants/MyLearningConstants';
import { useCurrentMyLearningTab } from 'bundles/unified-home-common/contexts/MyLearningTabContext';
import { usePageSize } from 'bundles/unified-home-common/contexts/PageSizeContext';
import OrgScopedLearnersLatestSkillProgress from 'bundles/unified-home-common/queries/skills/OrgScopedLearnersLatestSkillProgress.graphql';
import OrgScopedQueryAdminSelectedSkills from 'bundles/unified-home-common/queries/skills/OrgScopedQueryAdminSelectedSkills.graphql';
import OrgScopedQueryIfLearnerHasSkillProgress from 'bundles/unified-home-common/queries/skills/OrgScopedQueryIfLearnerHasSkillProgress.graphql';
import OrgScopedQueryProgressForTrendingSkills from 'bundles/unified-home-common/queries/skills/OrgScopedQueryProgressForTrendingSkills.graphql';
import type {
  OrgScopedLearnersLatestSkillProgressQuery,
  OrgScopedLearnersLatestSkillProgressQueryVariables,
} from 'bundles/unified-home-common/queries/skills/__generated__/OrgScopedLearnersLatestSkillProgress';
import type {
  OrgScopedQueryAdminSelectedSkillsQuery,
  OrgScopedQueryAdminSelectedSkillsQueryVariables,
} from 'bundles/unified-home-common/queries/skills/__generated__/OrgScopedQueryAdminSelectedSkills';
import type {
  OrgScopedQueryIfLearnerHasSkillProgressQuery,
  OrgScopedQueryIfLearnerHasSkillProgressQueryVariables,
} from 'bundles/unified-home-common/queries/skills/__generated__/OrgScopedQueryIfLearnerHasSkillProgress';
import type {
  OrgScopedQueryProgressForTrendingSkillsQuery,
  OrgScopedQueryProgressForTrendingSkillsQueryVariables,
} from 'bundles/unified-home-common/queries/skills/__generated__/OrgScopedQueryProgressForTrendingSkills';
import { getSkillProgressSortOption } from 'bundles/unified-home-common/utils/SkillDashboardUtils';

/**
 * Org-scoped version of EnterpriseSkillsProvider from page-config-common/providers/enterprise/EnterpriseSkillsProvider.tsx.
 *
 * Differences from the original:
 * - Uses orgSlug instead of programId for SkillsProgress queries
 * - Uses orgId instead of programId for SkillsTracking.queryByOrganization
 * - No useProgram() dependency
 */
const OrgScopedSkillsProvider = ({ skillLimit, children }: React.PropsWithChildren<{ skillLimit?: number }>) => {
  const thirdPartyOrg = useThirdPartyOrganization();
  const orgSlug = thirdPartyOrg?.slug;
  const orgId = thirdPartyOrg?.id;
  const location = useLocation();
  const { currentTab } = useCurrentMyLearningTab();
  const { pageSize, currentPageNum } = usePageSize();

  const lastRoutePath = location.pathname.slice(location.pathname.lastIndexOf('/') + 1);

  const isOnSkillsTab = currentTab === QUERY_PARAM_VALUES.SKILLS_DASHBOARD;
  const isOnLearnerHome = lastRoutePath === orgSlug && !currentTab;

  const isOnPageWithSkillsProgress = isOnLearnerHome || isOnSkillsTab;

  const {
    timeRange: timerangeInDays,
    skillLevel: skillLevelFilter,
    includeOnlyCompletedLevelSets: includeOnlySkillsWithCompletedLevelsets,
    sortField = 'UPDATED_AT',
  } = location.query;

  const { loading: isHasSkillProgressDataLoading, data: hasSkillProgressData } = useQuery<
    OrgScopedQueryIfLearnerHasSkillProgressQuery,
    OrgScopedQueryIfLearnerHasSkillProgressQueryVariables
  >(OrgScopedQueryIfLearnerHasSkillProgress, {
    variables: {
      orgSlug: orgSlug!,
    },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-and-network',
    skip: !orgSlug || !isOnPageWithSkillsProgress || !get?.()?.id,
    errorPolicy: 'all',
    ssr: false,
  });

  const hasSkillProgress =
    !!hasSkillProgressData?.SkillsProgress?.queryLearnersLatestSkillProgressByOrg?.hasProgressInProgram;

  const {
    data: latestSkills,
    networkStatus: latestSkillsNetworkStatus,
    loading: areLatestSkillsLoading,
    error: latestSkillsProgressError,
  } = useQuery<OrgScopedLearnersLatestSkillProgressQuery, OrgScopedLearnersLatestSkillProgressQueryVariables>(
    OrgScopedLearnersLatestSkillProgress,
    {
      variables: {
        orgSlug: orgSlug!,
        includeOnlySkillsWithCompletedLevelsets: includeOnlySkillsWithCompletedLevelsets === 'true',
        limit: skillLimit || pageSize,
        cursor: ((currentPageNum - 1) * pageSize).toString(),
        sortOption: getSkillProgressSortOption(sortField),
        timerangeInDays: parseInt(timerangeInDays, 10),
        skillLevelFilter,
      },
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'cache-and-network',
      skip: !orgSlug || !isOnPageWithSkillsProgress || !get?.()?.id || sortField === 'RELEVANCY',
      errorPolicy: 'all',
      ssr: false,
      notifyOnNetworkStatusChange: true,
    }
  );

  const latestSkillsWithProgress = React.useMemo(
    () => latestSkills?.SkillsProgress?.queryLearnersLatestSkillProgressByOrg?.elements ?? [],
    [latestSkills?.SkillsProgress?.queryLearnersLatestSkillProgressByOrg?.elements]
  );
  const totalLatestSkills =
    latestSkills?.SkillsProgress?.queryLearnersLatestSkillProgressByOrg?.pagination?.totalElements ?? 0;

  const {
    data: trendingSkills,
    networkStatus: trendingSkillsStatus,
    loading: trendingSkillsLoading,
    error: trendingSkillsError,
  } = useQuery<OrgScopedQueryProgressForTrendingSkillsQuery, OrgScopedQueryProgressForTrendingSkillsQueryVariables>(
    OrgScopedQueryProgressForTrendingSkills,
    {
      variables: {
        orgSlug: orgSlug!,
        limit: skillLimit || pageSize,
        cursor: ((currentPageNum - 1) * pageSize).toString(),
      },
      context: { clientName: 'gatewayGql' },
      fetchPolicy: 'cache-and-network',
      skip: !orgSlug || !get?.()?.id || sortField === 'RELEVANCY',
      errorPolicy: 'all',
      ssr: false,
      notifyOnNetworkStatusChange: true,
    }
  );

  const { data: adminSelectedSkillsData, loading: adminSelectedSkillsLoading } = useQuery<
    OrgScopedQueryAdminSelectedSkillsQuery,
    OrgScopedQueryAdminSelectedSkillsQueryVariables
  >(OrgScopedQueryAdminSelectedSkills, {
    variables: {
      orgId: orgId!,
      limit: skillLimit || pageSize,
    },
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-and-network',
    skip: !orgId || !get?.()?.id,
    errorPolicy: 'all',
    ssr: false,
  });

  // If there are no latest skills, fallback to trending skills
  const shouldUseLatestSkills = areLatestSkillsLoading || hasSkillProgress || isHasSkillProgressDataLoading;
  const networkStatus = shouldUseLatestSkills ? latestSkillsNetworkStatus : trendingSkillsStatus;
  const trendingSkillsWithProgress = React.useMemo(
    () => trendingSkills?.SkillsProgress?.queryProgressForTrendingSkillsInOrg?.elements ?? [],
    [trendingSkills?.SkillsProgress?.queryProgressForTrendingSkillsInOrg?.elements]
  );
  const totalTrendingSkills =
    trendingSkills?.SkillsProgress?.queryProgressForTrendingSkillsInOrg?.pagination?.totalElements ?? 0;
  const adminSelectedSkills = React.useMemo(
    () => adminSelectedSkillsData?.SkillsTracking?.queryByOrganization?.elements?.map(({ skill }) => skill),
    [adminSelectedSkillsData?.SkillsTracking?.queryByOrganization?.elements]
  );
  const hasProgramSpecificSkills = !!adminSelectedSkills?.length;

  // If there is an error, hasSkillProgress will always be false so it incorrectly returns trending skills
  const hasError = Boolean(latestSkillsProgressError || trendingSkillsError);
  const contextValue = React.useMemo(() => {
    return {
      latestSkillsWithProgress: shouldUseLatestSkills ? latestSkillsWithProgress : trendingSkillsWithProgress,
      hasSkillProgress,
      areLatestSkillsLoading: networkStatus ? networkStatus < 7 : false,
      totalLatestSkills: shouldUseLatestSkills ? totalLatestSkills : totalTrendingSkills,
      hasProgramSpecificSkills,
      adminSelectedSkills: hasProgramSpecificSkills
        ? adminSelectedSkills
        : trendingSkillsWithProgress.map(({ skill }) => skill),
      areAdminSelectedSkillsLoading: adminSelectedSkillsLoading || trendingSkillsLoading,
      hasError,
    };
  }, [
    adminSelectedSkills,
    adminSelectedSkillsLoading,
    hasError,
    hasProgramSpecificSkills,
    hasSkillProgress,
    latestSkillsWithProgress,
    networkStatus,
    shouldUseLatestSkills,
    totalLatestSkills,
    totalTrendingSkills,
    trendingSkillsLoading,
    trendingSkillsWithProgress,
  ]);

  return (
    <OrgScopedEnterpriseSkillsContext.Provider value={contextValue}>
      {children}
    </OrgScopedEnterpriseSkillsContext.Provider>
  );
};

export default OrgScopedSkillsProvider;
